import { Avatar, Box, IconButton, SvgIcon, Typography } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { borderRadius, boxShadows, palette, spaces } from '../../../assets/styles/themes/tokens';
import getMuiIcon, { type IconName } from '../../../utils/getMuiIcon';
import getTextTruncationSx from '../../../utils/getTextTruncationSx';

export const CHAT_MESSAGE_ARTIFACT_CARD_VARIANT = {
	DOCUMENT: 'document',
	PODCAST: 'podcast',
} as const;

export type ChatMessageArtifactCardVariant =
	(typeof CHAT_MESSAGE_ARTIFACT_CARD_VARIANT)[keyof typeof CHAT_MESSAGE_ARTIFACT_CARD_VARIANT];

// Per-variant differences. The card design is otherwise identical across
// variants — only the leading icon, its size and the default test id change.
// The podcast icon is a play button that reads as a 35px control inside a
// slightly larger box; it is a real button only when onPlayToggle is passed,
// otherwise the whole card remains the sole click target.
const VARIANT_CONFIG: Record<
	ChatMessageArtifactCardVariant,
	{ icon: IconName; defaultTestId: string; iconSize: string; boxSize: string }
> = {
	[CHAT_MESSAGE_ARTIFACT_CARD_VARIANT.DOCUMENT]: {
		icon: 'chatMessageArtifactDocument',
		defaultTestId: 'document-card',
		iconSize: '24px',
		boxSize: '40px',
	},
	[CHAT_MESSAGE_ARTIFACT_CARD_VARIANT.PODCAST]: {
		icon: 'playCircle',
		defaultTestId: 'podcast-card',
		iconSize: '35px',
		boxSize: '44px',
	},
};

export interface ChatMessageArtifactCardProps {
	title: string;
	subtitle?: string;
	onClick?: () => void;
	testId?: string;
	maxWidth?: string;
	isActive?: boolean;
	variant?: ChatMessageArtifactCardVariant;
	onPlayToggle?: () => void;
	isPlaying?: boolean;
	isPlayDisabled?: boolean;
}

// Mirrors the extended card's play button; the SVGs carry their own color,
// so disabled is conveyed via opacity.
const playButtonStyles = {
	padding: 0,
	'&.Mui-disabled': {
		opacity: 0.4,
	},
};

// Compact card shown in the chat for a generated artifact (document, podcast, …).
const ChatMessageArtifactCard = React.forwardRef<HTMLDivElement, ChatMessageArtifactCardProps>(
	(
		{
			title,
			subtitle,
			onClick,
			testId,
			isActive = false,
			variant = CHAT_MESSAGE_ARTIFACT_CARD_VARIANT.DOCUMENT,
			onPlayToggle,
			isPlaying = false,
			isPlayDisabled = false,
		},
		ref
	) => {
		const { t } = useTranslation();
		const { icon, defaultTestId, iconSize, boxSize } = VARIANT_CONFIG[variant];

		return (
			<Box
				ref={ref}
				onClick={onClick}
				data-testid={testId ?? defaultTestId}
				sx={{
					display: 'flex',
					alignItems: 'flex-start',
					gap: spaces.xs,
					padding: spaces.sm,
					width: '100%',
					backgroundColor: palette.background.paperElevation2,
					outline: isActive ? `2px solid ${palette.primary.main}` : 'none',
					borderRadius: borderRadius.md,
					cursor: onClick ? 'pointer' : 'default',
					// Only the hover shadow transitions; the active outline (palette.primary.main) applies immediately.
					transition: 'box-shadow 0.2s ease-in-out',
					boxSizing: 'border-box',
					boxShadow: boxShadows.elevation1,
					'&:hover': onClick
						? {
								boxShadow: boxShadows.elevation2,
							}
						: {},
				}}
			>
				<Box
					sx={{
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
						paddingRight: spaces.xxs,
					}}
				>
					{onPlayToggle ? (
						<IconButton
							data-testid="podcast-card-play-button"
							onClick={(event) => {
								// The whole card opens the detail view; playback must not also trigger it.
								event.stopPropagation();
								onPlayToggle();
							}}
							disabled={isPlayDisabled}
							aria-label={isPlaying ? t('Pause') : t('Play')}
							sx={{ ...playButtonStyles, width: boxSize, height: boxSize }}
						>
							<SvgIcon
								component={getMuiIcon(isPlaying ? 'pauseCircle' : icon)}
								sx={{ fontSize: iconSize }}
								aria-hidden="true"
							/>
						</IconButton>
					) : (
						<Avatar
							sx={{
								width: boxSize,
								height: boxSize,
								backgroundColor: 'transparent',
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
							}}
						>
							<SvgIcon
								component={getMuiIcon(icon)}
								sx={{
									color: palette.text.secondary,
									fontSize: iconSize,
								}}
							/>
						</Avatar>
					)}
				</Box>
				<Box
					sx={{
						display: 'flex',
						flexDirection: 'column',
						flexGrow: 1,
						minWidth: 0,
					}}
				>
					<Typography
						variant="subtitle1"
						sx={{
							fontWeight: 500,
							lineHeight: '28px',
							width: '100%',
							color: palette.text.primary,
							...getTextTruncationSx(1),
						}}
					>
						{title}
					</Typography>
					{subtitle && (
						<Typography
							variant="caption"
							sx={{
								color: palette.text.disabled,
								width: '100%',
								...getTextTruncationSx(1),
							}}
						>
							{subtitle}
						</Typography>
					)}
				</Box>
			</Box>
		);
	}
);

export default ChatMessageArtifactCard;
